import { ReactElement } from "react";
import { NextPage } from "next";
import { AppProps } from "next/app";
import { ApolloProvider } from "@apollo/client";
import { ChakraProvider } from "@chakra-ui/react";
import Favicon from "components/Layout/PageHead/Favicon";
import HeadTitle from "components/Layout/PageHead/HeadTitle";
import ErrorBoundary from "components/Layout/ErrorBoundary";
import ToastUnhandledErrorWrapper from "components/UI/Notifications/ToastUnhandledError";
import { AuthProvider } from "lib/auth/auth";
import { useSegmentTrackPage } from "lib/integrations/segment";
import CubeAuthProvider from "lib/integrations/cube/CubeAuthProvider";
import PageAccessGate from "components/Layout/PageAccessGate";
import theme from "theme";
import client from "lib/apollo/client";
import useReleaseCheck from "components/Layout/useReleaseCheck";
import { ToastContainer } from "components/UI/Notifications/standaloneToast";
import NoAccountModal from "components/Layout/BrandSettings/NoAccountModal";

import "styles/global.css";
import "styles/react-datepicker.scss";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";

import "@fontsource/work-sans/300.css";

import "@fontsource-variable/figtree";

import "@fontsource/noto-sans-mono/300.css";
import "@fontsource/noto-sans-mono/400.css";
import { useInitUserback } from "lib/integrations/userbackContext";

type NextPageWithLayout = NextPage & {
  /** Used to define the page layout that should be used when rendering this
   * page. */
  getLayout?: (page: ReactElement) => ReactElement;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  useSegmentTrackPage();
  useInitUserback();
  useReleaseCheck();
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  return (
    <ApolloProvider client={client}>
      <ChakraProvider theme={theme} resetCSS>
        <ToastUnhandledErrorWrapper>
          <ErrorBoundary>
            <AuthProvider>
              <Favicon />
              <HeadTitle />

              <CubeAuthProvider>
                <PageAccessGate>
                  {getLayout(<Component {...pageProps} />)}
                </PageAccessGate>
              </CubeAuthProvider>

              <NoAccountModal />
            </AuthProvider>
          </ErrorBoundary>
        </ToastUnhandledErrorWrapper>
        <ToastContainer />
      </ChakraProvider>
    </ApolloProvider>
  );
}
